import { Alert, Button, Card, Form, Input, Select, Space, Typography } from "antd";
import type { Customer, CustomerVo } from "@zhihaoscm/entity";
import { useEffect, useState, type ComponentProps } from "react";
import { useNavigate, useParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DatePicker as ZhDatePicker,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    InputNumber as ZhInputNumber
} from "zhihao-ui-react";

import request from "@/api";
import type { CustomerAdvancePaymentForm } from "@/entity/expenseManage/customerAdvancePayment";
import type { PlatformBankAccount } from "@/entity/expenseManage/platBankAccount";
import { getInfoFromCustomerJsonInfo } from "@/utils/getInfoFromCustomer";
import { useUserStore } from "@/stores/react/authStore";

export interface UserAccountAdvanceFormPageProps {
    customerId?: number | string;
    loadPlatformAccounts?: () => Promise<PlatformBankAccount[]>;
    loadCustomerAccounts?: (keyword: string) => Promise<Customer[]>;
    loadCustomerAccount?: (id: number | string) => Promise<Customer>;
    createAdvancePayment?: (params: CustomerAdvancePaymentForm) => Promise<unknown>;
}

interface AdvanceFormValues {
    customerAccountId?: number | string;
    amount?: number | string;
    date?: string;
    platformAccountId?: string;
    name?: string;
    account?: string;
    openingBank?: string;
}

// beta.13 类型声明未暴露 controls，扩展类型以保留旧 Vue 金额输入的无步进配置。
type AdvanceMoneyInputProps = ComponentProps<typeof ZhInputNumber> & { controls?: boolean };

// 保留旧 Vue el-input-number 的金额范围与两位小数契约。
const advanceMoneyInputProps: AdvanceMoneyInputProps = {
    controls: false,
    max: 10000000000,
    min: 1,
    precision: 2
};

/** 复用 Vue 平台银行账号选择器。 */
export async function loadAdvancePlatformAccounts() {
    const response = await request.service.get<PlatformBankAccount[]>("/admin/platform/bank-account/selector", {
        params: {}
    });
    return response.data;
}

/** 复用 Vue 用户账户新增接口，保持 POST 路径和嵌套字段结构。 */
export async function createUserAdvancePayment(params: CustomerAdvancePaymentForm) {
    const response = await request.service.post("/customer/advance-payment", params);
    return response.data;
}

/** 复用 Vue 用户账号远程搜索接口。 */
export async function searchAdvanceCustomerAccounts(keyword: string) {
    const response = await request.service.get<Customer[]>("/admin/customer/selector", { params: { param: keyword } });
    return response.data;
}

/** 复用 Vue 带客户 ID 进入表单时的详情回显接口。 */
export async function loadAdvanceCustomerAccount(id: number | string) {
    const response = await request.service.get<CustomerVo>(`/admin/customer/find/${id}`);
    return response.data.customer;
}

/** 费用管理-用户账户-新增预付款，保持 Vue 表单字段、边界和提交契约。 */
export function UserAccountAdvanceFormPage({
    customerId,
    loadPlatformAccounts = loadAdvancePlatformAccounts,
    loadCustomerAccounts = searchAdvanceCustomerAccounts,
    loadCustomerAccount = loadAdvanceCustomerAccount,
    createAdvancePayment = createUserAdvancePayment
}: UserAccountAdvanceFormPageProps) {
    const routeParams = useParams<{ customerId?: string }>();
    const navigate = useNavigate();
    const banButton = useUserStore(state => state.banButton);
    const routeCustomerId = customerId ?? routeParams.customerId;
    const [form] = Form.useForm<AdvanceFormValues>();
    const [platformAccounts, setPlatformAccounts] = useState<PlatformBankAccount[]>([]);
    const [customerAccounts, setCustomerAccounts] = useState<Customer[]>([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [saving, setSaving] = useState(false);
    const [customerSearching, setCustomerSearching] = useState(false);

    useEffect(() => {
        let disposed = false;
        void loadPlatformAccounts()
            .then(accounts => {
                if (!disposed) setPlatformAccounts(accounts);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("平台银行账号加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadPlatformAccounts]);

    useEffect(() => {
        if (routeCustomerId == null) return;
        form.setFieldValue("customerAccountId", routeCustomerId);
    }, [form, routeCustomerId]);

    useEffect(() => {
        if (routeCustomerId == null) return;
        let disposed = false;
        void loadCustomerAccount(routeCustomerId)
            .then(customer => {
                if (!disposed) setCustomerAccounts([customer]);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("用户账号加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadCustomerAccount, routeCustomerId]);

    const [selectedPlatformAccount, setSelectedPlatformAccount] = useState<PlatformBankAccount>();

    const searchCustomers = async (keyword: string) => {
        if (!keyword.trim() || routeCustomerId != null) return;
        setCustomerSearching(true);
        try {
            setCustomerAccounts(await loadCustomerAccounts(keyword.trim()));
        } catch {
            setErrorMessage("用户账号加载失败");
        } finally {
            setCustomerSearching(false);
        }
    };

    const handleFinish = async (values: AdvanceFormValues) => {
        const platform = platformAccounts.find(account => String(account.id) === String(values.platformAccountId));
        if (!platform) {
            setErrorMessage("请选择平台银行账号");
            return;
        }
        setSaving(true);
        setErrorMessage("");
        try {
            await createAdvancePayment({
                amount: values.amount == null ? undefined : Number(values.amount),
                date: values.date || "",
                customerAccountId: values.customerAccountId || "",
                platformAccount: {
                    id: platform.id,
                    name: platform.name,
                    account: platform.account,
                    openingBank: platform.openingBank
                },
                advancePaymentAccount: {
                    name: values.name || "",
                    account: values.account || "",
                    openingBank: values.openingBank || ""
                }
            });
            void navigate(-1);
        } catch {
            setErrorMessage("预付款新增失败");
        } finally {
            setSaving(false);
        }
    };

    return (
        <section aria-label="user-account-advance-form-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Typography.Title className="mb-6 mt-0" level={4}>
                    新增预付款
                </Typography.Title>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                <Form<AdvanceFormValues>
                    form={form}
                    layout="vertical"
                    initialValues={routeCustomerId == null ? undefined : { customerAccountId: routeCustomerId }}
                    onFinish={values => void handleFinish(values)}
                >
                    <ZhGrid cols={4} gap={[32, 0]}>
                        <Form.Item
                            label="用户账号"
                            name="customerAccountId"
                            rules={[{ required: true, message: "请选择用户账户" }]}
                        >
                            {routeCustomerId != null ? (
                                <Select
                                    aria-label="用户账号"
                                    disabled
                                    options={customerAccounts.map(customer => ({
                                        // 路由参数来自 URL 字符串，统一选项值类型以确保禁用下拉正确回显名称。
                                        value: String(customer.id),
                                        label: getInfoFromCustomerJsonInfo(customer)
                                    }))}
                                    placeholder="请选择用户账号"
                                />
                            ) : (
                                <Select
                                    allowClear
                                    aria-label="用户账号"
                                    showSearch
                                    filterOption={false}
                                    loading={customerSearching}
                                    onSearch={value => void searchCustomers(value)}
                                    options={customerAccounts.map(customer => ({
                                        value: customer.id,
                                        label: getInfoFromCustomerJsonInfo(customer)
                                    }))}
                                    placeholder="请选择用户账号"
                                />
                            )}
                        </Form.Item>
                        <ZhGridItem span={3} />
                        <Form.Item
                            label="金额"
                            name="amount"
                            rules={[
                                { required: true, message: "请输入金额" },
                                {
                                    validator: (_, value: number | string | undefined) => {
                                        const amount = Number(value);
                                        return amount >= 1 && amount <= 10000000000 && Number.isFinite(amount)
                                            ? Promise.resolve()
                                            : Promise.reject(new Error("金额范围1~10000000000"));
                                    }
                                }
                            ]}
                        >
                            <ZhInputNumber {...advanceMoneyInputProps} aria-label="金额" placeholder="请输入金额" />
                        </Form.Item>
                        <Form.Item label="日期" name="date" rules={[{ required: true, message: "请选择日期" }]}>
                            <ZhDatePicker
                                aria-label="日期"
                                format="YYYY-MM-DD"
                                placeholder="请选择日期"
                                type="date"
                                valueFormat="YYYY-MM-DD"
                            />
                        </Form.Item>
                        <ZhGridItem span={3}>
                            <Form.Item
                                label="平台银行账号"
                                name="platformAccountId"
                                rules={[{ required: true, message: "请选择平台银行账号" }]}
                            >
                                <Select
                                    allowClear
                                    aria-label="平台银行账号"
                                    className="w-full"
                                    optionFilterProp="label"
                                    options={platformAccounts.map(account => ({
                                        label: account.name,
                                        value: account.id
                                    }))}
                                    placeholder="请选择平台银行账号"
                                    showSearch
                                    onChange={selectedId => {
                                        // 选择平台账号后同步只读银行信息，保持 Vue onPlatChange 的回显行为。
                                        setSelectedPlatformAccount(
                                            platformAccounts.find(account => String(account.id) === String(selectedId))
                                        );
                                    }}
                                />
                            </Form.Item>
                            {selectedPlatformAccount ? (
                                <div className="bank-info mb-5 mt-2 flex w-full gap-10 rounded-md bg-[#fafafa] p-5">
                                    <ZhBaseItem label="开户名称" value={selectedPlatformAccount.name} />
                                    <ZhBaseItem label="银行账号" value={selectedPlatformAccount.account} />
                                    <ZhBaseItem label="开户行" value={selectedPlatformAccount.openingBank} />
                                </div>
                            ) : null}
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <Typography.Title className="mt-6" level={5}>
                                用户预付款银行账号
                            </Typography.Title>
                        </ZhGridItem>
                        <Form.Item label="开户名称" name="name" rules={[{ required: true, message: "请输入开户名称" }]}>
                            <Input aria-label="开户名称" maxLength={25} placeholder="请输入开户名称" />
                        </Form.Item>
                        <Form.Item
                            label="银行账号"
                            name="account"
                            rules={[
                                { required: true, message: "请输入银行账号" },
                                { pattern: /^[0-9]{8,30}$/, message: "银行账号范围8~30" }
                            ]}
                        >
                            <Input aria-label="银行账号" maxLength={30} placeholder="请输入银行账户" />
                        </Form.Item>
                        <Form.Item
                            label="开户行"
                            name="openingBank"
                            rules={[{ required: true, message: "请输入开户行" }]}
                        >
                            <Input aria-label="开户行" maxLength={128} placeholder="请输入开户行" />
                        </Form.Item>
                        <ZhGridItem span={4}>
                            <Space className="operation-fixed-btn" size={0}>
                                <Button disabled={banButton} htmlType="submit" loading={saving} type="primary">
                                    提交
                                </Button>
                                <Button htmlType="button" type="primary" onClick={() => void navigate(-1)}>
                                    返回
                                </Button>
                            </Space>
                        </ZhGridItem>
                    </ZhGrid>
                </Form>
            </Card>
        </section>
    );
}

export default UserAccountAdvanceFormPage;
